<template>
    <form @submit.prevent="createOrder"
          class="col-md-10 ms-auto me-auto pb-5"
          :style="!customer?.addresses?.shipping ? 'opacity: 0.5; pointer-events: none;' : ''">
      <div class="order-entry-table-wrap">
        <table class="table table-order order-entry-table" :class="{ 'with-prices': prices }">
          <thead>
          <tr>
            <th class="">Položka</th>
            <th class="" v-if="prices">Cena bez DPH</th>
            <th class="">Počet k objednání</th>
            <th class=""></th>
            <th class="">Počet k reklamaci</th>
            <th class=""></th>
            <th class="">Množství v obalu</th>
          </tr>
          </thead>
          <tbody>
          <!---->
          <tr v-if="visibleProducts.length===0">
            <td :colspan="prices ? 7 : 6">
              <div class="spinner-border" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
            </td>
          </tr>
          <tr v-for="product in visibleProducts" :key="product.id">
            <td class="name">
              {{ product.name }}
              <span class="bg-warning rounded-pill px-2 text-white py-1 me-1" v-if="customer?.addresses?.shipping.custom?.akce !== null && product?.custom['akce'+customer?.addresses?.shipping.custom?.akce] === 1">
            <small>Akce</small>
          </span>

              <small>{{ product.code }}</small>
              <small v-if="!product.orderable" class="d-block text-muted">mimo sezónu – jen odpis</small></td>
            <td v-if="prices" class="price text-nowrap">{{ formatPrice(prices, product) }}</td>
            <td>
              <input type="text"
                     @input="onDecimalInput($event,product,false)"
                     @keydown.enter.prevent="focusNextInput($event)"
                     :disabled="!product.orderable"
                     placeholder="0">
            </td>
            <td>
              <select v-model="product.orderUnit" :disabled="!product.orderable">
                <option v-for="unit in unitTypes" :value="unit">{{ unit }}</option>
              </select>
            </td>
            <td>
              <input type="text"
                     @input="onDecimalInput($event,product, true)"
                     @keydown.enter.prevent="focusNextInput($event)"
                     :disabled="product.reclamationAllowedCount === 0"
                     placeholder="0">
            </td>
            <td>{{ product.unit }}</td>
            <td class="obal">{{ product.custom.mnobal }} {{ product.unit }}</td>
          </tr>
          </tbody>
        </table>
      </div>

      <div class="clear clearfix"></div>
      <div v-if="minimum && parentOrders.length" class="order-type mb-3">
        <div class="fw-bold mb-2">Co posíláte?</div>
        <div class="order-type-options">
          <label class="order-type-option" :class="{ active: orderType === 'order' }">
            <input class="visually-hidden" type="radio" id="orderTypeOrder" value="order" v-model="orderType">
            <span class="d-block fw-bold">Novou objednávku</span>
            <small class="text-muted">platí minimum {{ formatCzk(minimum) }} bez DPH</small>
          </label>
          <label class="order-type-option" :class="{ active: orderType === 'supplement' }">
            <input class="visually-hidden" type="radio" id="orderTypeSupplement" value="supplement" v-model="orderType">
            <span class="d-block fw-bold">Dodatečnou objednávku</span>
            <small class="text-muted">doplnění objednávky č. {{ parentOrders[0].counterNumber }}, minimum neplatí</small>
          </label>
        </div>
      </div>

      <div v-if="prices" class="order-summary">
        <div class="d-flex justify-content-between align-items-baseline gap-3">
          <span class="fw-bold">Orientační cena bez DPH</span>
          <span class="order-total">{{ formatCzk(total.total) }}</span>
        </div>
        <template v-if="minimum && orderType === 'order'">
          <div class="progress" role="progressbar" :aria-valuenow="total.total" aria-valuemin="0" :aria-valuemax="minimum">
            <div class="progress-bar" :class="shortfall > 0 || !hasOrderedItems ? 'bg-warning' : 'bg-success'"
                 :style="{ width: Math.min(100, total.total / minimum * 100) + '%' }"></div>
          </div>
          <small class="minimum-status d-block" :class="{ 'text-success': hasOrderedItems && shortfall === 0 }">{{ minimumStatus }}</small>
        </template>
        <small v-else-if="orderType === 'supplement'" class="minimum-status d-block">
          Dodatečná objednávka k č. {{ selectedParent?.counterNumber }}, minimum neplatí.
        </small>
        <small v-if="total.missing.length" class="d-block text-warning-emphasis">
          Bez ceny, do součtu se nepočítá: {{ total.missing.join(', ') }}
        </small>
      </div>
      <p v-if="prices" class="order-summary-note text-muted">{{ orientationalPriceNote }}</p>

          <div class="input-group">
            <span class="input-group-text bg-light-green fw-bold">Poznámka k objednávce:</span> <br/>
            <textarea class="form-control" v-model="note"/>
          </div>
      <br>
      <div class="input-group">
        <span class="input-group-text bg-light-green fw-bold">Zvolte datum doručení:&nbsp&nbsp</span>
        <input class="form-control" type="date" v-model="deliveryDate" :min="minDeliveryDate"
               :disabled="!!lockedDeliveryDate"/>
      </div>
      <small v-if="lockedDeliveryDate" class="d-block text-start text-muted mt-1 locked-delivery-date">
        Dodatečná objednávka se doručí spolu s objednávkou č. {{ selectedParent?.counterNumber }}, datum nejde změnit.
      </small>
      <br>

      <p>Před odesláním si prosím <strong>zkontrolujte správnost všech údajů. <br/> Objednávku již nelze
        editovat.</strong> <br/> Děkujeme</p>
      <br/>

      <button type="submit"
              :disabled="shortfall > 0"
              class="btn btn-lg btn-primary text-white rounded-0 w-100">
        <span class="spinner-grow spinner-grow-sm" v-if="creating" aria-hidden="true"></span>
        {{ shortfall > 0 ? `Do minima chybí ${formatCzk(shortfall)}` : 'Uložit objednávku' }}
      </button>
    </form>
</template>
<script lang="ts">
import {useAppStore} from "@/stores/appStore";
import {useNotificationStore} from "@/stores/notificationStore";
import AddressSelect from "@/components/gira/addressSelect.vue";
import * as Sentry from "@sentry/vue";
import {
  BaseOrderStatus,
  OrderCreationRequest,
  OrderItemNoOrderId, type ResponseEntityOrder
} from "@haxo-cz/orders-ts-client";
import {
  customerPrices,
  formatCzk,
  formatPrice,
  hideForbidden,
  loadOrderRows,
  minimumOrder,
  orderTotal,
  type OrderRow,
  supplementParents
} from "@/components/gira/orderRows";

export default {
  components: {AddressSelect},
  emits: ['orderCreated'],
  data() {
    return {
      appStore: useAppStore(),
      notificationStore: useNotificationStore(),
      products: [] as OrderRow[],
      deliveryDate: null as string | null,
      minDeliveryDate: "",
      note: "",
      unitTypes: ["kus", "kg", "bal", "sv", "plato"],
      creating: false,
      orderType: "order" as "order" | "supplement",
      parentOrderId: null as string | null,
      parentOrders: [] as any[],
      orientationalPriceNote: "Cena je orientační. Konečná cena se řídí skutečně dodaným množstvím, které se může od objednaného lišit (např. podle velikosti balení).",
    }
  },
  methods: {
    formatPrice,
    formatCzk,
    resolveAddressId(address: any): string | undefined {
      if (!address) return undefined;
      if (typeof address === "string") return address;
      if (typeof address === "object") {
        return address._id || address.id || undefined;
      }
      return undefined;
    },
    focusNextInput(event: KeyboardEvent) {
      const form = (event.target as HTMLElement).closest('form');
      if (!form) return;
      const inputs = Array.from(form.querySelectorAll('input[type="text"]:not(:disabled)')) as HTMLInputElement[];
      const idx = inputs.indexOf(event.target as HTMLInputElement);
      if (idx >= 0 && idx < inputs.length - 1) {
        inputs[idx + 1].focus();
      }
    },
    onDecimalInput(event: Event, product, reclamation) {
      const input = event.target as HTMLInputElement;
      // Povolit jen čísla, tečku a max jednu tečku
      let value = input.value.replace(',', '.'); // nahradí čárku za tečku
      value = value.replace(/[^0-9.]/g, '');      // odstraní nepovolené znaky

      const parts = value.split('.');
      if (parts.length > 2) {
        // Více než jedna tečka – ponech první a zbytek odstraní
        value = parts[0] + '.' + parts.slice(1).join('');
      }

      input.value = value;

      let numericValue = parseFloat(value);
      // console.log(numericValue);
      if (reclamation){
        const max = product.reclamationAllowedCount ?? 0;
        if (!isNaN(numericValue)) {
          if (numericValue > max){
            numericValue = max;
            product.reclamationCount = numericValue;
            input.value = numericValue.toString();
          }else{
            product.reclamationCount = value;
          }
        } else {
          product.reclamationCount = '';
        }
      } else{
        product.orderCount = value;
      }
    },
    toDateInputValue(date: Date): string {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },
    getTomorrowDateInputValue(): string {
      const tomorrow = new Date();
      tomorrow.setHours(0, 0, 0, 0);
      tomorrow.setDate(tomorrow.getDate() + 1);
      return this.toDateInputValue(tomorrow);
    },
    async createOrder() {
      this.creating = true;
      const orderedProducts = [];

      if (this.deliveryDate && this.deliveryDate < this.minDeliveryDate && !this.lockedDeliveryDate) {
        this.creating = false;
        alert("Datum doručení musí být nejdříve zítra.");
        return;
      }

      this.visibleProducts.forEach(product => {
        if (product.orderCount > 0) {
          const _product = {
            code: product.code,
            name: product.name,
            quantity: product.orderCount,
            unit: product.unit, // unit dle produktu
            price: product.price,
            custom: {
              reclamation: false,
              orderUnit: product.orderUnit // unit dle změny
            }
          }
          orderedProducts.push(_product);
        }
        if( product.reclamationCount > 0){
          const _product = {
            code: product.code,
            name: product.name,
            quantity: product.reclamationCount,
            unit: product.unit, // unit dle produktu
            price: product.price,
            custom: {
              reclamation: true,
            }
          }
          orderedProducts.push(_product);
        }
      });

      if(orderedProducts.length === 0) {
        this.creating = false;
        alert('Prosím zadejte alespoň jednu položku k objednání nebo reklamaci.');
        return;
      }

      if (this.shortfall > 0) {
        this.creating = false;
        alert(`Minimální objednávka je ${formatCzk(this.minimum ?? 0)} bez DPH, chybí ${formatCzk(this.shortfall)}.`);
        return;
      }
      const newOrder = {
        custom: {
          note: this.note,
          deliveryDate: this.deliveryDate,
          userId: this.appStore.mongoUser._id,
          ...(this.minimum ? {orderType: this.orderType} : {}),
          ...(this.minimum && this.orderType === 'supplement' ? {parentOrderId: this.parentOrderId} : {}),
        },
        orderedAt: new Date(),
        status: BaseOrderStatus.Created,
        customer: {
          email: this.customer?.email,
          phone: this.customer?.phone,
          name: this.customer?.name
        },
        addresses: {
          billing: this.resolveAddressId(this.customer?.addresses?.billing),
          shipping: this.resolveAddressId(this.customer?.addresses?.shipping)
        },
        items: orderedProducts
      }
      if(!this.appStore.selectedProject){
        this.creating = false;
        return;
      }
      await this.$ordersApi.createOrder(
          this.appStore.selectedProject.spaceId,
          this.appStore.selectedProject._id,
          newOrder as OrderCreationRequest
      ).then(response=>{
        this.note = "";
        this.deliveryDate = null;
        this.orderType = "order";
        this.parentOrderId = null;
        this.creating = false;
        this.sendOrderEmail(response.data);
        this.$router.push({name: 'order-created', params: {orderId: response.data.id}});
      }).catch(e=>{
        this.creating = false;
        console.log(e)
        this.notificationStore.error(
            e?.response?.data?.message ?? "Objednávku se nepodařilo uložit, zkuste to prosím znovu.",
            "Objednávka neuložena"
        );
      })

    },
    sendOrderEmail(order: ResponseEntityOrder){
      this.$modulesGiraApi.sendOrderEmail(
          this.appStore.selectedProject.spaceId,
          this.appStore.selectedProject._id,
          order.id
      ).catch(() => {
        this.notificationStore.error("Potvrzovací email se nepodařilo odeslat.", "Chyba emailu");
      });
    },
    // Zákazník vidí jen své objednávky, stačí posledních 36 hodin a filtr na dnešek a prodejnu.
    async loadParentOrders() {
      const project = this.appStore.selectedProject;
      if (!project || !this.minimum)
        return;
      try {
        const response = await this.$ordersApi.findAllOrders(
            project.spaceId,
            project._id,
            1,
            50,
            new Date(Date.now() - 36 * 60 * 60 * 1000).toISOString(),
            undefined,
            undefined,
            JSON.stringify({userId: this.appStore.mongoUser?._id}),
        );
        this.parentOrders = supplementParents(response.data.data, this.customer?.addresses?.shipping);
      } catch (error) {
        Sentry.captureException(error);
        this.parentOrders = [];
      }
    },
    async loadRows() {
      const project = this.appStore.selectedProject;
      if(!project)
        return;
      try {
        this.products = await loadOrderRows(
            () => this.$productsApi.findAllProducts(
                project.spaceId,
                project._id,
                1, // currentPage
                5000, // limit – načítáme i mimosezónní produkty kvůli odpisům
                undefined, // dateFrom
                undefined, // dateTo
                undefined, // tag
                undefined, // search
                undefined, // bez filtru validity – odpis musí jít i u zboží mimo sezónu
                'name', // sortBy
                'asc' // sortOrder
            ).then(response => response.data.data),
            () => this.$modulesGiraApi.findReclamationProducts(
                project.spaceId,
                project._id,
            ).then(response => response.data),
            error => {
              Sentry.captureException(error);
              this.notificationStore.error(
                  "Nepodařilo se načíst položky k reklamaci, reklamaci teď nelze zadat. Objednávku odeslat můžete, pro reklamaci obnovte stránku.",
                  "Chyba reklamací"
              );
            }
        );
      } catch (error) {
        console.error('Error fetching products:', error);
      }
    },
  },
  async mounted() {
    this.minDeliveryDate = this.getTomorrowDateInputValue();
    // API klienti vznikají až po přihlášení; bez čekání padá mount na undefined $modulesGiraApi
    if (!await this.$apisReady.catch(() => false))
      return;
    await Promise.all([this.loadRows(), this.loadParentOrders()]);
    // console.log(this.customer?.addresses?.shipping?.ico)
  },
  computed: {
    customer() {
      return this.appStore.customer;
    },
    visibleProducts() {
      return hideForbidden(this.products, this.customer?.addresses?.shipping);
    },
    prices() {
      return customerPrices(this.customer?.addresses?.shipping);
    },
    minimum() {
      return minimumOrder(this.customer?.addresses?.shipping);
    },
    total() {
      return orderTotal(this.visibleProducts, this.prices);
    },
    hasOrderedItems(): boolean {
      return this.visibleProducts.some(product => Number(product.orderCount) > 0);
    },
    shortfall(): number {
      if (!this.minimum || this.orderType !== 'order' || !this.hasOrderedItems) return 0;
      return Math.max(0, Math.round((this.minimum - this.total.total) * 100) / 100);
    },
    minimumStatus(): string {
      if (!this.minimum) return '';
      if (!this.hasOrderedItems) return `Minimální objednávka je ${formatCzk(this.minimum)} bez DPH.`;
      return this.shortfall > 0
          ? `Do minima ${formatCzk(this.minimum)} chybí ${formatCzk(this.shortfall)}.`
          : `Minimum ${formatCzk(this.minimum)} je splněno.`;
    },
    selectedParent(): any {
      return this.parentOrders.find(order => order.id === this.parentOrderId) ?? null;
    },
    // Dodatečná objednávka jede se stejným závozem jako ta, ke které patří.
    lockedDeliveryDate(): string | null {
      const date = this.orderType === 'supplement' ? this.selectedParent?.custom?.deliveryDate : null;
      return date ? String(date).slice(0, 10) : null;
    },
  },
  watch: {
    // Bez výběru: dodatečná objednávka patří k poslední dnešní objednávce prodejny.
    orderType(type: string) {
      this.parentOrderId = type === 'supplement' ? this.parentOrders[0]?.id ?? null : null;
    },
    lockedDeliveryDate(date: string | null) {
      if (date) this.deliveryDate = date;
    },
  }
}
</script>
